// Normalization utilities for Orders and Order Items
// This prepares incoming API data (which may include File blobs for images) into
// a consistent internal shape consumed by UI components.
// Image strategy:
//  - If thumbnail is a URL (string), we keep as-is.
//  - If thumbnail is a File/Blob, we store it in IndexedDB via imageStore and keep the key.
//  - UI components receive either { url } or { key } and must resolve key to object URL lazily.

import imageStore from '../services/imageStore';

export async function normalizeOrder(raw) {
  if (!raw) return null;
  const products = await Promise.all((raw.products || []).map(normalizeOrderItem));
  const invoice = await normalizeInvoice(raw.invoice);
  return {
    id: String(raw.id),
    code: raw.code || `#${raw.id}`,
    createdAt: raw.createdAt || new Date().toISOString(),
    status: raw.status || 'AGUARDANDO PAGAMENTO',
    type: raw.type || 'Mercadoria',
    payment: normalizePayment(raw.payment),
    delivery: normalizeDelivery(raw.delivery),
    pricing: normalizePricing(raw.pricing, products),
    invoice,
    products
  };
}

export async function normalizeOrderItem(item) {
  if (!item) return null;
  let thumb = null;
  if (typeof item.thumbnail === 'string') {
    thumb = { url: item.thumbnail };
  } else if (item.thumbnail instanceof Blob || item.thumbnail instanceof File) {
    try {
      const key = await imageStore.saveImage(item.thumbnail);
      thumb = { key };
    } catch {
      thumb = null;
    }
  } else if (item.thumbnail && item.thumbnail.key) {
    thumb = { key: item.thumbnail.key };
  }
  return {
    id: String(item.id),
    name: item.name || 'Produto',
    variant: item.variant || '',
    thumbnail: thumb, // { url } or { key } or null
    productionStatus: item.productionStatus || 'PENDENTE',
    color: item.color || '',
    style: item.style || '',
    weight: item.weight || '',
    type: item.type || '',
    unitPrice: Number(item.unitPrice || 0),
    quantity: Number(item.quantity || 1),
    discountAmount: Number(item.discountAmount || 0),
    subtotal: Number(item.unitPrice || 0) * Number(item.quantity || 1),
    total: Number(item.unitPrice || 0) * Number(item.quantity || 1) - Number(item.discountAmount || 0)
  };
}

function normalizePayment(pay = {}) {
  return {
    environment: pay.environment || 'Presencial',
    method: pay.method || 'Dinheiro',
    paidAt: pay.paidAt || new Date().toISOString(),
    amountPaid: Number(pay.amountPaid || 0),
    change: Number(pay.change || 0),
    paymentCode: pay.paymentCode || 'PAY-XXXXX'
  };
}

function normalizeDelivery(del = {}) {
  return {
    method: del.method || 'Retirar na loja',
    maxPickupDate: del.maxPickupDate || new Date(Date.now() + 7*86400_000).toISOString()
  };
}

function normalizePricing(pr = {}, products) {
  const subtotal = products.reduce((s, p) => s + p.subtotal, 0);
  return {
    subtotal: pr.subtotal != null ? Number(pr.subtotal) : subtotal,
    shipping: Number(pr.shipping || 0),
    discounts: Number(pr.discounts || 0),
    total: pr.total != null ? Number(pr.total) : subtotal + Number(pr.shipping || 0) - Number(pr.discounts || 0)
  };
}

async function normalizeInvoice(inv) {
  if (!inv) return null;
  // If invoice is a URL string, keep as-is
  if (typeof inv === 'string') {
    return { url: inv, filename: 'nota_fiscal.pdf' };
  }
  // If invoice is a File/Blob (from API), store in IndexedDB
  if (inv instanceof Blob || inv instanceof File) {
    try {
      const key = await imageStore.saveImage(inv);
      return { key, filename: inv.name || 'nota_fiscal.pdf' };
    } catch {
      return null;
    }
  }
  // If already normalized { url, filename } or { key, filename }
  if (inv.url || inv.key) {
    return {
      url: inv.url || null,
      key: inv.key || null,
      filename: inv.filename || 'nota_fiscal.pdf'
    };
  }
  return null;
}

export async function normalizeOrders(list) {
  return Promise.all((list || []).map(normalizeOrder));
}

// Helper to resolve thumbnail object to a usable URL if needed.
export async function resolveThumbnail(thumb) {
  if (!thumb) return null;
  if (thumb.url) return thumb.url;
  if (thumb.key) {
    return await imageStore.getImageUrl(thumb.key);
  }
  return null;
}

export default { normalizeOrder, normalizeOrders, normalizeOrderItem, resolveThumbnail };
